import '../styles/score-cards.css';

import dayjs from 'dayjs';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenue } from '@/domain/impact-calculate-venue/entities/calculate-venue.entity';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import {
  selectImpactCalculateVenueCo2ForProject,
  selectImpactCalculateVenueForProject,
} from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import EventsIconSvg from '../../pdf/assets/events_icon.svg';
import GroupPeopleSvg from '../../pdf/assets/group_people.svg';
import { displayDecimal } from '../../pdf/utils/display-scores.utils';
import ArrowIcon from '../assets/arrow.svg';
import CrossImg from '../assets/cross.svg';
import FairlyScorePillLogo from '../assets/fairly-score-pill-logo.svg';
import { MainScorePill } from '../components/MainScorePill';

export function ScoreCardsVenueC2() {
  document.documentElement.classList.add('score-card');
  document.body.classList.add('score-card');
  dayjs.locale('fr');

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(
    selectCurrentProjectVenue,
  ) satisfies ProjectVenue | null;
  const venueId = venue?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateVenueCo2ForProject(venueId),
  ) satisfies CalculateVenueCo2;

  const impactCalculateVenue = useAppSelector(
    selectImpactCalculateVenueForProject(venueId),
  ) satisfies CalculateVenue | null;

  const dates = impactCalculateVenue?.count?.dates ?? 0;
  const participants = impactCalculateVenue?.count?.participants ?? 0;

  const totalStructureEmissions =
    impactCalculateVenue?.co2?.structure?.score ?? 0;
  const totalDatesEmissions = impactCalculateVenue?.co2?.dates?.score ?? 0;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  return (
    <div id="card2" className="score-card-section">
      <div className="title">
        <img
          src={FairlyScorePillLogo}
          width="80"
          height="80"
          alt="Fairly logo"
        />
        <img src={CrossImg} width="30" height="30" />
        <h1>{venue.name}</h1>
      </div>
      <div className="scores">
        <div className="subtitle">
          <div className="score-row-left">
            <img src={ArrowIcon} width="40" height="40" />
            <span>{translate('Projects.co2')}</span>
          </div>
          <MainScorePill
            scoreLetter={impactCo2.global.scoreLetter}
            type="co2"
          />
        </div>
        <div className="score-row highlight">
          <span>{translate('PdfExports.total-emissions')}</span>
          <span>
            <span className="highlight">
              {displayDecimal(impactCo2.global.score)}
            </span>{' '}
            {translate('Projects.teCO2')}
          </span>
        </div>
        <div className="score-row">
          <span>{translate('Projects.emissions-by-participant')}</span>
          <span>
            {displayDecimal(impactCo2.global.kgeCO2ByParticipant)}{' '}
            <span style={{ fontSize: '0.8em' }}>
              {translate('Projects.kgeCO2')}
            </span>
          </span>
        </div>
        <div className="score-row">
          <span>{translate('PdfExports.date-emissions').split('(')[0]}</span>
          <span>
            {displayDecimal(totalDatesEmissions)}{' '}
            <span style={{ fontSize: '0.8em' }}>
              {translate('Projects.teCO2')}
            </span>
          </span>
        </div>
        <div className="score-row">
          <span>
            {translate('PdfExports.structure-emissions').split('(')[0]}
          </span>
          <span>
            {displayDecimal(totalStructureEmissions)}{' '}
            <span style={{ fontSize: '0.8em' }}>
              {translate('Projects.teCO2')}
            </span>
          </span>
        </div>
      </div>

      <div className="scores">
        <h3 style={{ margin: 0, lineHeight: '1.1em' }}>
          {translate('PdfExports.dates-and-public')}
        </h3>
        <div style={{ padding: '8px 15px' }}>
          <div>
            <img
              src={GroupPeopleSvg}
              width="28"
              height="28"
              style={{ position: 'relative', top: 2, marginRight: 8 }}
              alt="Calendar icon"
            />
            <span>{participants}</span>
            <span>
              {' '}
              {translate('PdfExports.participants', {
                count: participants,
              })}
            </span>
          </div>
          <div>
            <img
              src={EventsIconSvg}
              width="28"
              height="28"
              style={{ position: 'relative', top: 2, marginRight: 8 }}
              alt="Calendar icon"
            />
            <span>{dates}</span>
            <span> {translate('PdfExports.events', { count: dates })}</span>
          </div>
        </div>
      </div>
      <div className="edition-date">
        {translate('PdfExports.edition-date', { date: dayjs().format('LL') })}
      </div>
    </div>
  );
}
